import React, { useState } from 'react';
import axios from 'axios';
import { X, Loader2, Plus, Trash2, IndianRupee, CheckCircle2, MessageCircle } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { fmtINR, formatErr, notifyErr, inputCls } from './shared';

const todayISO = () => new Date().toISOString().slice(0, 10);
const fmtDate = (s) => {
  if (!s) return '';
  const d = new Date(s.length === 10 ? `${s}T00:00:00` : s);
  return Number.isNaN(d.getTime()) ? s : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const METHOD_OPTIONS = ['UPI', 'Cash', 'NEFT', 'IMPS', 'RTGS', 'Cheque', 'Card', 'Other'];

/**
 * Modal for recording payments against an invoice. Shows running balance,
 * the list of recorded payments (with delete), and a form to add new ones.
 * Refreshes parent via `onSaved(updatedInvoice)` so the surrounding form
 * picks up the new status/amount_paid without a full reload.
 */
const RecordPaymentModal = ({ invoice, onClose, onSaved, apiBase, onSendThanks }) => {
  const { authHeader } = useAuth();
  // Accepts a per-instance API base so this modal can drive both non-GST
  // (`${API}/billing/invoices`) and GST (`${API}/gst/invoices`) endpoints.
  const base = apiBase || `${API}/billing/invoices`;
  // GST invoices carry total_due in `totals.grand_total`; non-GST carry it
  // as `total_due` at the top level. Fall back gracefully so callers don't
  // need to pre-shape the object.
  const isGst = base.includes('/gst/');
  const totalDue = Number(
    invoice.total_due
    ?? invoice?.totals?.grand_total
    ?? 0,
  ) - (isGst ? Number(invoice.advance_received || 0) : 0);
  const paid = Number(invoice.amount_paid || 0);
  const balance = Math.max(0, totalDue - paid);

  const [form, setForm] = useState({
    amount: balance > 0 ? balance.toFixed(2) : '',
    date: todayISO(),
    method: 'UPI',
    reference: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const refresh = async () => {
    try {
      const { data } = await axios.get(`${base}/${invoice.id}`, { headers: authHeader() });
      onSaved && onSaved(data);
      return data;
    } catch (e) {
      console.warn('refresh invoice failed', e?.message);
    }
  };

  const save = async (e) => {
    e?.preventDefault();
    setErr('');
    const amt = Number(form.amount);
    if (!amt || amt <= 0) { setErr('Enter an amount greater than 0.'); return; }
    setSaving(true);
    try {
      await axios.post(
        `${base}/${invoice.id}/payments`,
        {
          amount: amt,
          date: form.date,
          method: form.method,
          reference: form.reference,
          notes: form.notes,
        },
        { headers: authHeader() },
      );
      setForm({ ...form, amount: '', reference: '', notes: '' });
      const updated = await refresh();
      if (updated && onSendThanks) onSendThanks(updated, { amount: amt, method: form.method, reference: form.reference });
    } catch (e2) {
      setErr(formatErr(e2));
    } finally {
      setSaving(false);
    }
  };

  const removePayment = async (pid) => {
    if (!window.confirm('Remove this payment? The invoice status will recompute.')) return;
    try {
      await axios.delete(
        `${base}/${invoice.id}/payments/${pid}`,
        { headers: authHeader() },
      );
      await refresh();
    } catch (e) {
      notifyErr(e);
    }
  };

  const payments = invoice.payments || [];
  const advance = isGst ? Number(invoice.advance_received || 0) : 0;
  const fullyPaid = paid >= totalDue - 0.005 && (totalDue > 0 || advance > 0);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="record-payment-modal"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 sticky top-0 bg-white">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Record payment</h2>
            <p className="text-xs text-slate-500 mt-0.5">Invoice {invoice.number}</p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100" data-testid="record-payment-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Summary chips */}
          <div className="grid grid-cols-3 gap-3" data-testid="payment-summary">
            <Chip label={advance > 0 ? 'Due after advance' : 'Invoice total'} value={`₹ ${fmtINR(totalDue)}`} />
            <Chip label="Already paid" value={`₹ ${fmtINR(paid)}`} accent={paid > 0 ? 'emerald' : 'slate'} />
            <Chip label="Balance" value={`₹ ${fmtINR(balance)}`} accent={balance > 0 ? 'amber' : 'emerald'} />
          </div>

          {fullyPaid ? (
            <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2">
              <CheckCircle2 size={16} /> Invoice fully paid. You can still log additional payments below if needed.
            </div>
          ) : null}

          {/* New payment form */}
          <form onSubmit={save} className="bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid="payment-form">
            <div className="grid grid-cols-12 gap-2">
              <div className="col-span-6 sm:col-span-3">
                <label className="block text-[11px] uppercase tracking-wider text-slate-500 mb-1">Amount *</label>
                <div className="relative">
                  <IndianRupee size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="number" step="0.01" min="0.01"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    className={`${inputCls} pl-7`}
                    placeholder="0.00"
                    data-testid="payment-amount"
                  />
                </div>
              </div>
              <div className="col-span-6 sm:col-span-3">
                <label className="block text-[11px] uppercase tracking-wider text-slate-500 mb-1">Date</label>
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  className={inputCls}
                  data-testid="payment-date"
                />
              </div>
              <div className="col-span-6 sm:col-span-3">
                <label className="block text-[11px] uppercase tracking-wider text-slate-500 mb-1">Method</label>
                <select
                  value={form.method}
                  onChange={(e) => setForm({ ...form, method: e.target.value })}
                  className={inputCls}
                  data-testid="payment-method"
                >
                  {METHOD_OPTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div className="col-span-6 sm:col-span-3">
                <label className="block text-[11px] uppercase tracking-wider text-slate-500 mb-1">Reference / Txn ID</label>
                <input
                  value={form.reference}
                  onChange={(e) => setForm({ ...form, reference: e.target.value })}
                  className={inputCls}
                  placeholder="UPI ref / cheque no."
                  data-testid="payment-reference"
                />
              </div>
              <div className="col-span-12">
                <label className="block text-[11px] uppercase tracking-wider text-slate-500 mb-1">Notes</label>
                <input
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className={inputCls}
                  placeholder="Optional — e.g. via GPay to 9916120561"
                  data-testid="payment-notes"
                />
              </div>
            </div>
            {err && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1 mt-2">{err}</div>}
            <div className="flex justify-end mt-3">
              <button
                type="submit"
                disabled={saving || !form.amount}
                className="inline-flex items-center gap-1.5 bg-teal-500 hover:bg-teal-600 text-white text-sm font-medium px-3 py-2 rounded-lg disabled:opacity-50"
                data-testid="payment-save"
              >
                {saving ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />} Record payment
              </button>
            </div>
          </form>

          {/* Payment history */}
          <div>
            <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-2">Payment history ({payments.length})</h3>
            {payments.length === 0 ? (
              <p className="text-sm text-slate-400 italic text-center py-4">No payments recorded yet.</p>
            ) : (
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm" data-testid="payments-history">
                  <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
                    <tr>
                      <th className="text-left px-3 py-2">Date</th>
                      <th className="text-left px-3 py-2">Method</th>
                      <th className="text-left px-3 py-2 hidden sm:table-cell">Reference</th>
                      <th className="text-right px-3 py-2">Amount</th>
                      <th className="text-right px-3 py-2 w-12"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((p) => (
                      <tr key={p.id} className="border-t border-slate-200" data-testid={`payment-row-${p.id}`}>
                        <td className="px-3 py-2 text-slate-600">{fmtDate(p.date)}</td>
                        <td className="px-3 py-2 text-slate-700">{p.method || '—'}</td>
                        <td className="px-3 py-2 text-slate-600 hidden sm:table-cell truncate max-w-[180px]">
                          {p.reference || (p.notes ? <span className="italic text-slate-400">{p.notes}</span> : '—')}
                        </td>
                        <td className="px-3 py-2 text-right font-medium text-slate-900">₹ {fmtINR(p.amount)}</td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          {onSendThanks && (
                            <button
                              onClick={() => onSendThanks(invoice, p)}
                              className="p-1 rounded hover:bg-emerald-50 text-emerald-700"
                              title="Send thank-you on WhatsApp"
                              data-testid={`payment-send-thanks-${p.id}`}
                            >
                              <MessageCircle size={13} />
                            </button>
                          )}
                          <button
                            onClick={() => removePayment(p.id)}
                            className="p-1 rounded hover:bg-red-50 text-red-600"
                            title="Remove payment"
                            data-testid={`payment-remove-${p.id}`}
                          >
                            <Trash2 size={13} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const Chip = ({ label, value, accent = 'slate' }) => {
  const cls = {
    slate: 'bg-slate-50 border-slate-200 text-slate-900',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
  }[accent];
  return (
    <div className={`border rounded-lg px-3 py-2 ${cls}`}>
      <div className="text-[11px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-base font-semibold mt-0.5">{value}</div>
    </div>
  );
};

export default RecordPaymentModal;
